"use client";

import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { InfoBubble } from "./InfoBubble";

/** The searchable groups, in the order results are shown. */
export type SearchKind =
  | "applications"
  | "vendors"
  | "functions"
  | "workflows"
  | "steps";

/** What the dialog needs of a result; it hands the original object back. */
export interface SearchDialogResult {
  id: string;
  title: string;
  subtitle: string;
  /** Why an indirect match surfaced, e.g. a mapped workflow's name. */
  via?: { label: string };
}

export interface SearchDialogGroup<R extends SearchDialogResult> {
  kind: SearchKind;
  /** Distinct matches before the result cap. */
  total: number;
  /** True when `results` holds fewer rows than `total`. */
  truncated: boolean;
  results: R[];
}

/**
 * What the dialog shows for a nonblank query. `ready` groups are rendered in
 * the order given; any other status replaces the rows entirely, so results
 * that no longer match the query are never on screen to be picked.
 */
export type SearchDialogState<R extends SearchDialogResult> =
  | { status: "loading" }
  | { status: "ready"; groups: SearchDialogGroup<R>[] }
  | {
      status:
        | "forbidden"
        | "not-provisioned"
        | "not-found"
        | "invalid"
        | "error";
    };

export interface SearchDialogProps<R extends SearchDialogResult> {
  query: string;
  onQueryChange: (query: string) => void;
  state: SearchDialogState<R>;
  onSelect: (result: R) => void;
  onClose: () => void;
  /** Offered with the `error` status. */
  onRetry?: () => void;
  /** The longest query the backend accepts. */
  maxLength?: number;
  /** Where focus lands on close if nothing meaningful held it on open. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Extra content under the result groups, e.g. a prototype-only section. */
  supplement?: ReactNode;
  /** Extra initial content; the render callback supplies a focus-safe example action. */
  initialSupplement?:
    | ReactNode
    | ((onExample: (query: string) => void) => ReactNode);
  /** Accessible name for the dialog and its input. */
  label?: string;
}

const INITIAL_SHOWN = 3;
const EXPAND_STEP = 5;

const FOCUSABLE =
  'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

const CATEGORY: Record<
  SearchKind,
  {
    label: string;
    chipLabel: string;
    hint: string;
    example: string;
    className: string;
    info?: string;
  }
> = {
  applications: {
    label: "Applications",
    chipLabel: "Application",
    hint: "by application or product name",
    example: "Infusion pumps",
    className:
      "border-[color-mix(in_oklab,var(--accent-cobalt)_28%,var(--border-subtle))] bg-(--accent-cobalt-bg) text-(--accent-cobalt-text)",
  },
  vendors: {
    label: "Vendors",
    chipLabel: "Vendor",
    hint: "by vendor or manufacturer",
    example: "Epic",
    className:
      "border-[color-mix(in_oklab,var(--accent-grape)_28%,var(--border-subtle))] bg-(--accent-grape-bg) text-(--accent-grape-text)",
  },
  functions: {
    label: "Business functions",
    chipLabel: "Business function",
    hint: "by business function",
    example: "Pharmacy",
    className:
      "border-[color-mix(in_oklab,var(--accent-rose)_28%,var(--border-subtle))] bg-(--accent-rose-bg) text-(--accent-rose-text)",
    info: "The business area work rolls up to. A workflow can span more than one.",
  },
  workflows: {
    label: "Workflows",
    chipLabel: "Workflow",
    hint: "a group of related steps",
    example: "Prior authorization",
    className:
      "border-[color-mix(in_oklab,var(--accent-teal)_28%,var(--border-subtle))] bg-(--accent-teal-bg) text-(--accent-teal-text)",
    info: "A group of related workflow steps.",
  },
  steps: {
    label: "Workflow steps",
    chipLabel: "Workflow step",
    hint: "a single step within a workflow",
    example: "Inventory",
    className:
      "border-[color-mix(in_oklab,var(--accent-brown)_28%,var(--border-subtle))] bg-(--accent-brown-bg) text-(--accent-brown-text)",
    info: "A single step within a workflow.",
  },
};

const KINDS = Object.keys(CATEGORY) as SearchKind[];

/** Wording follows the map pages' notices (`MAP_COPY` in apps/web). */
const NOTICES: Record<
  Exclude<
    SearchDialogState<SearchDialogResult>["status"],
    "ready" | "loading" | "error"
  >,
  { title: string; body: string }
> = {
  forbidden: {
    title: "Explorer data unavailable",
    body: "You don't have access to Explorer data.",
  },
  "not-provisioned": {
    title: "Search isn't ready yet",
    body: "We haven't loaded your organization's applications into Explorer yet. Your Elion team will let you know when it's ready.",
  },
  "not-found": {
    title: "Search data unavailable",
    body: "The selected Explorer data doesn't exist, or it has moved.",
  },
  invalid: {
    title: "That search isn't valid",
    body: "Shorten or change your search and try again.",
  },
};

/**
 * The Explorer command-palette search: a modal with a query box, five
 * color-coded result groups that expand a few rows at a time, and states for
 * loading, no matches, and each way a search can fail.
 *
 * It is presentation only. The parent owns the query and fetches; render the
 * dialog only while it is open, so state resets on every open. Rows are never
 * re-sorted or re-filtered here.
 */
export function SearchDialog<R extends SearchDialogResult>({
  query,
  onQueryChange,
  state,
  onSelect,
  onClose,
  onRetry,
  maxLength = 200,
  returnFocusRef,
  supplement,
  initialSupplement,
  label = "Search Explorer",
}: SearchDialogProps<R>) {
  const baseId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const scrollActive = useRef(false);
  const returnFocus = useRef(returnFocusRef);
  returnFocus.current = returnFocusRef;
  const [entered, setEntered] = useState(false);
  const [shown, setShown] = useState<Partial<Record<SearchKind, number>>>({});
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const hasQuery = query.trim().length > 0;

  const visibleGroups = useMemo(
    () =>
      state.status === "ready"
        ? state.groups.map((group) => ({
            group,
            visible: group.results.slice(0, shown[group.kind] ?? INITIAL_SHOWN),
          }))
        : [],
    [state, shown],
  );
  const rows = useMemo(
    () =>
      visibleGroups.flatMap(({ group, visible }) =>
        visible.map((result) => ({
          key: `${group.kind}:${result.id}`,
          result,
        })),
      ),
    [visibleGroups],
  );
  // The active row is a key, not a position, so a changed result set can only
  // move it to a row that exists. An unknown key falls back to the first row.
  const found = rows.findIndex((row) => row.key === activeKey);
  const activeIndex = rows.length === 0 ? -1 : Math.max(found, 0);
  const selectable = hasQuery && state.status === "ready" && rows.length > 0;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  useEffect(() => {
    const previous = document.activeElement;
    inputRef.current?.focus();
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => {
      cancelAnimationFrame(frame);
      const target =
        previous instanceof HTMLElement &&
        previous !== document.body &&
        previous.isConnected
          ? previous
          : returnFocus.current?.current;
      target?.focus({ preventScroll: true });
    };
  }, []);

  // Keyboard moves only: hovering a row must not scroll the list under it.

  useEffect(() => {
    if (!scrollActive.current || activeIndex < 0) return;
    scrollActive.current = false;
    document
      .getElementById(`${baseId}-option-${activeIndex}`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, baseId]);

  const changeQuery = (next: string) => {
    setShown({});
    setActiveKey(null);
    onQueryChange(next);
  };

  const selectExample = (example: string) => {
    inputRef.current?.focus();
    changeQuery(example);
  };

  const move = (to: number) => {
    const index = Math.min(Math.max(to, 0), rows.length - 1);
    const row = rows[index];
    scrollActive.current = false;
    if (!row || index === activeIndex) return;
    scrollActive.current = true;
    setActiveKey(row.key);
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (selectable) move(activeIndex + (event.key === "ArrowDown" ? 1 : -1));
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = selectable ? rows[activeIndex] : undefined;
      if (row) onSelect(row.result);
    }
  };

  const onDialogKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = [
      ...(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []),
    ].filter((element) => !element.hasAttribute("disabled"));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) {
      event.preventDefault();
      return;
    }
    const current = document.activeElement;
    if (
      event.shiftKey &&
      (current === first || current === dialogRef.current)
    ) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && current === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const hasRows = state.status === "ready" && state.groups.length > 0;
  let rowIndex = -1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-[12vh]">
      <button
        type="button"
        aria-label="Close search"
        tabIndex={-1}
        onClick={onClose}
        className={`absolute inset-0 cursor-default bg-black/72 transition-opacity duration-200 ease-in-out motion-reduce:transition-none ${
          entered ? "opacity-100" : "opacity-0"
        }`}
      />

      <div
        ref={dialogRef}
        role="dialog"
        aria-label={label}
        aria-modal="true"
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onFocus={(event) => {
          // A click on non-interactive content focuses the dialog itself; hand
          // focus back to the input so arrows and Escape keep working.
          if (event.target === event.currentTarget) inputRef.current?.focus();
        }}
        className={`relative flex h-[36rem] max-h-[84vh] w-full max-w-[46rem] origin-top flex-col overflow-hidden rounded-(--radius-container) border border-(--border-subtle) bg-(--bg-white) shadow-2xl outline-none transition-[opacity,transform] duration-200 ease-in-out motion-reduce:transition-none ${
          entered ? "scale-100 opacity-100" : "scale-[0.97] opacity-0"
        }`}
      >
        <div className="flex items-center gap-2.5 border-b border-(--divider-row) px-4 py-3">
          <SearchIcon />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-label={label}
            aria-expanded={hasRows && hasQuery}
            aria-controls={
              state.status === "ready" && hasQuery
                ? state.groups
                    .map((group) => `${baseId}-list-${group.kind}`)
                    .join(" ") || undefined
                : undefined
            }
            aria-activedescendant={
              selectable ? optionId(activeIndex) : undefined
            }
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            maxLength={maxLength}
            value={query}
            onChange={(event) => changeQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search applications, vendors, business functions, workflows, steps"
            className="w-full bg-transparent text-[14px] text-(--fg-primary) outline-none placeholder:text-(--fg-muted)"
          />
          <kbd className="shrink-0 rounded border border-(--border-subtle) px-1.5 py-0.5 text-[10px] text-(--fg-muted)">
            Esc
          </kbd>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-2">
          {!hasQuery ? (
            <>
              <Examples onExample={selectExample} />
              {typeof initialSupplement === "function"
                ? initialSupplement(selectExample)
                : initialSupplement}
            </>
          ) : state.status === "loading" ? (
            <p
              role="status"
              className="px-4 py-6 text-[12.5px] text-(--fg-secondary)!"
            >
              Searching…
            </p>
          ) : state.status === "ready" ? (
            <>
              {state.groups.length === 0 && !supplement && (
                <p
                  role="status"
                  className="px-4 py-6 text-[12.5px] text-(--fg-secondary)!"
                >
                  No matches for “{query.trim()}”.
                </p>
              )}
              {state.groups.length > 0 && (
                <p role="status" className="sr-only">
                  {state.groups.reduce((sum, group) => sum + group.total, 0)}{" "}
                  matches in {state.groups.length}{" "}
                  {state.groups.length === 1 ? "category" : "categories"}
                </p>
              )}
              {visibleGroups.map(({ group, visible }) => {
                const meta = CATEGORY[group.kind];
                const remaining = group.results.length - visible.length;
                return (
                  <div key={group.kind} className="mb-1.5">
                    <div className="flex items-center gap-1.5 px-4 pb-1 pt-3 first:pt-1">
                      <span
                        className={`inline-flex items-center rounded-(--radius-full) border px-2 py-0.5 text-[10.5px] font-semibold ${meta.className}`}
                      >
                        {meta.label}
                      </span>
                      {meta.info && <InfoBubble text={meta.info} />}
                    </div>
                    <div
                      id={`${baseId}-list-${group.kind}`}
                      role="listbox"
                      aria-label={meta.label}
                    >
                      {visible.map((result) => {
                        rowIndex += 1;
                        const index = rowIndex;
                        return (
                          <Row
                            key={`${group.kind}:${result.id}`}
                            id={optionId(index)}
                            result={result}
                            active={index === activeIndex}
                            onHover={() => {
                              scrollActive.current = false;
                              setActiveKey(rows[index]?.key ?? null);
                            }}
                            onOpen={() => onSelect(result)}
                          />
                        );
                      })}
                    </div>
                    {remaining > 0 && (
                      <button
                        type="button"
                        onMouseDown={keepInputFocus}
                        onClick={() => {
                          inputRef.current?.focus();
                          setShown((current) => ({
                            ...current,
                            [group.kind]:
                              (current[group.kind] ?? INITIAL_SHOWN) +
                              EXPAND_STEP,
                          }));
                        }}
                        aria-label={`Show ${Math.min(EXPAND_STEP, remaining)} more ${meta.label.toLowerCase()}`}
                        className="ml-4 mt-0.5 cursor-pointer rounded-(--radius-field) px-2 py-1 text-[11.5px] font-medium text-(--fg-secondary) hover:bg-(--bg-hover) hover:text-(--fg-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)"
                      >
                        Show {Math.min(EXPAND_STEP, remaining)} more
                        <span className="text-(--fg-muted)">
                          {" "}
                          ({remaining})
                        </span>
                      </button>
                    )}
                    {group.truncated && (
                      <p className="px-4 pt-1 text-[11px] text-(--fg-muted)!">
                        Showing the top {group.results.length} of {group.total}{" "}
                        matches. Refine your search.
                      </p>
                    )}
                  </div>
                );
              })}
              {supplement}
            </>
          ) : state.status === "error" ? (
            <div
              role="alert"
              className="mx-4 my-2 rounded-(--radius-container) border border-(--status-error) bg-(--status-error-bg) p-6 text-center"
            >
              <p className="text-[12.5px] text-(--status-error-text)!">
                We couldn't complete your search. Try again.
              </p>
              {onRetry && (
                <button
                  type="button"
                  onClick={() => {
                    inputRef.current?.focus();
                    onRetry();
                  }}
                  className="mt-3 cursor-pointer rounded-(--radius-field) border border-(--border-subtle) bg-(--bg-white) px-2.5 py-1 text-[11.5px] font-medium text-(--fg-primary) hover:border-(--border-strong) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--fg-primary)"
                >
                  Try again
                </button>
              )}
            </div>
          ) : (
            <div role="status" className="px-4 py-6">
              <p className="text-[13px] font-semibold">
                {NOTICES[state.status].title}
              </p>
              <p className="mt-1! text-[12.5px] text-(--fg-secondary)!">
                {NOTICES[state.status].body}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Keeps the query input focused when a pointer press lands on a row. */
function keepInputFocus(event: { preventDefault: () => void }) {
  event.preventDefault();
}

function Row({
  id,
  result,
  active,
  onHover,
  onOpen,
}: {
  id: string;
  result: SearchDialogResult;
  active: boolean;
  onHover: () => void;
  onOpen: () => void;
}) {
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard selection runs through the combobox input (arrows + Enter).
    <div
      id={id}
      role="option"
      aria-selected={active}
      tabIndex={-1}
      onMouseDown={keepInputFocus}
      onMouseEnter={onHover}
      onClick={onOpen}
      className={`flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left ${
        active ? "bg-(--bg-hover)" : ""
      }`}
    >
      <span className="flex min-w-0 flex-1 flex-col items-start">
        <span className="max-w-full truncate text-[13px] font-medium text-(--fg-primary)">
          {result.title}
        </span>
        <span className="max-w-full truncate text-[11px] text-(--fg-muted)">
          {result.subtitle}
        </span>
        {result.via && (
          <span className="mt-1 inline-flex max-w-full items-center gap-1 rounded-(--radius-full) border border-(--border-subtle) bg-(--bg-canvas) px-1.5 py-0.5 text-[10px] font-medium text-(--fg-muted)">
            <CornerIcon />
            <span className="truncate">via {result.via.label}</span>
          </span>
        )}
      </span>
      <span className="shrink-0 text-(--fg-subtle)" aria-hidden={true}>
        →
      </span>
    </div>
  );
}

function Examples({ onExample }: { onExample: (query: string) => void }) {
  return (
    <div>
      <p className="px-4 pb-1 pt-1 text-[11.5px] font-semibold text-(--fg-primary)">
        Search your inventory
      </p>
      <p className="px-4 pb-2 text-[12px] leading-[1.5] text-(--fg-secondary)!">
        Jump to anything by name. Each result is tagged by category. Examples
        are illustrative; results depend on your organization's data.
      </p>
      {KINDS.map((kind) => {
        const meta = CATEGORY[kind];
        return (
          <button
            key={kind}
            type="button"
            onClick={() => onExample(meta.example)}
            className="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left hover:bg-(--bg-hover) focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-(--fg-primary)"
          >
            <span
              className={`inline-flex shrink-0 items-center rounded-(--radius-full) border px-2 py-0.5 text-[10.5px] font-semibold ${meta.className}`}
            >
              {meta.chipLabel}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12px] text-(--fg-secondary)">
              {meta.hint}
            </span>
            <span className="shrink-0 text-[12px] text-(--fg-muted)">
              e.g. “{meta.example}”
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="shrink-0 text-(--fg-muted)"
      aria-hidden={true}
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function CornerIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden={true}
    >
      <path d="M9 10v6a2 2 0 0 0 2 2h6" />
    </svg>
  );
}
